import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ImageUpload } from "@/components/ImageUpload";

const EMPTY = { title: "", description: "", image: "", link: "", published: true };

export default function AgriProductsManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/agri-products/all").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ title: p.title, description: p.description, image: p.image, link: p.link || "", published: p.published });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/agri-products/${editing.id}`, form);
      else await api.post("/agri-products", form);
      toast.success(editing ? "Attrezzo aggiornato" : "Attrezzo creato");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    await api.delete(`/agri-products/${id}`).catch(() => {});
    toast.success("Attrezzo eliminato");
    load();
  };

  const field = "border-line bg-sand/70 text-forest";

  return (
    <section data-testid="agri-manager">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-3xl text-forest">Vetrina AgriEuro</h2>
        <button onClick={openNew} data-testid="agri-new-button" className="flex items-center gap-2 rounded-full bg-forest px-5 py-2.5 font-accent text-xs uppercase tracking-[0.18em] text-bone transition-transform duration-300 hover:scale-105 active:scale-95">
          <Plus className="h-4 w-4" /> Nuovo attrezzo
        </button>
      </div>
      <p className="mt-2 text-sm text-sage">Incolla il tuo link tracciato AgriEuro nel campo "Link affiliato" di ogni prodotto.</p>
      <div className="mt-8 overflow-x-auto rounded-sm border border-line bg-bone">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-line font-accent text-[10px] uppercase tracking-[0.2em] text-sage">
              <th className="px-5 py-4">Prodotto</th>
              <th className="hidden px-5 py-4 md:table-cell">Link affiliato</th>
              <th className="px-5 py-4">Stato</th>
              <th className="px-5 py-4 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b border-line/60 last:border-0" data-testid={`agri-row-${p.id}`}>
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    <img src={p.image} alt="" className="h-10 w-10 rounded-sm bg-sand object-cover" />
                    <span className="font-medium text-forest">{p.title}</span>
                  </div>
                </td>
                <td className="hidden max-w-64 px-5 py-4 md:table-cell">
                  {p.link ? (
                    <a href={p.link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 truncate text-xs text-moss hover:text-terra">
                      <ExternalLink className="h-3 w-3 shrink-0" /> <span className="truncate">{p.link}</span>
                    </a>
                  ) : (
                    <span className="text-xs text-sage">Nessun link</span>
                  )}
                </td>
                <td className="px-5 py-4">
                  <span className={`rounded-full px-3 py-1 font-accent text-[10px] uppercase tracking-[0.15em] ${p.published ? "bg-moss/15 text-moss" : "bg-sand text-sage"}`}>
                    {p.published ? "Online" : "Bozza"}
                  </span>
                </td>
                <td className="px-5 py-4">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => openEdit(p)} className="rounded-full border border-line p-2 text-forest hover:bg-sand" data-testid={`agri-edit-${p.id}`} aria-label="Modifica"><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => del(p.id)} className="rounded-full border border-line p-2 text-destructive hover:bg-sand" data-testid={`agri-delete-${p.id}`} aria-label="Elimina"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <p className="px-5 py-10 text-center text-sage">Nessun attrezzo. Aggiungi il primo.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-bone" data-testid="agri-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-forest">{editing ? "Modifica attrezzo" : "Nuovo attrezzo"}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-5 pt-2">
            <div>
              <Label className="font-accent text-xs uppercase tracking-[0.18em] text-sage">Titolo *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={`mt-2 ${field}`} data-testid="agri-form-title" />
            </div>
            <div>
              <Label className="font-accent text-xs uppercase tracking-[0.18em] text-sage">Descrizione</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`mt-2 ${field}`} data-testid="agri-form-description" />
            </div>
            <ImageUpload label="Foto" value={form.image} onChange={(v) => setForm({ ...form, image: v })} testid="agri-form-image" />
            <div>
              <Label className="font-accent text-xs uppercase tracking-[0.18em] text-sage">Link affiliato (tracciato)</Label>
              <Input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://www.agrieuro.com/…?tuo-codice" className={`mt-2 ${field}`} data-testid="agri-form-link" />
              <p className="mt-1.5 text-xs text-sage">Incolla qui il tuo link tracciato: il pulsante "Vedi su AgriEuro" punterà a questo indirizzo.</p>
            </div>
            <label className="flex items-center gap-3 text-sm text-forest">
              <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="agri-form-published" /> Visibile sul sito
            </label>
            <button onClick={save} disabled={saving || !form.title} data-testid="agri-form-save" className="flex items-center justify-center gap-2 rounded-full bg-forest py-3.5 font-accent text-sm uppercase tracking-[0.2em] text-bone transition-transform duration-300 hover:scale-[1.01] active:scale-95 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Salva
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
